import { AppThunk, ApiError } from "../misc";
import { ClusterApi } from "@cluster/web-components";
import { doGetChannels } from "./channels";
import { doRoute } from "./location";
import { ClusterMessagesClient } from "../store";
import Log from "../../utils/Log";
import { AuthTokenData } from '../../lib/app-api'
import { redirect } from "redux-first-router";
import { doClearDeferredDestination } from "./system";
import firebase from '../../lib/firebase';
import { getJwtTokenPayload } from "../../utils/utils";
import { selectChannelIds, selectDeviceId, selectIsUserGuest, selectUser } from "../selectors";

export enum USER_ACTIONS {
  AUTH_USER_START = "AUTH_USER_START",
  AUTH_USER_SUCCESS = "AUTH_USER_SUCCESS",
  AUTH_USER_ERROR = "AUTH_USER_ERROR",
  UPDATE_USER_START = "UPDATE_USER_START",
  UPDATE_USER_SUCCESS = "UPDATE_USER_SUCCESS",
  UPDATE_USER_ERROR = "UPDATE_USER_ERROR",
  LOGOUT = "LOGOUT",
  REWRITE_TOKEN = "REWRITE_TOKEN",
  FAVORITES_SET = "FAVORITES_SET",
  FAVORITE_ADDED = "FAVORITE_ADDED",
  FAVORITE_REMOVED = "FAVORITE_REMOVED",
  AUTH_SOURCE_TOKEN_SET = "AUTH_SOURCE_TOKEN_SET",
  TOKEN_REFRESH_LOCKED = "TOKEN_REFRESH_LOCKED",
  TOKEN_REFRESH_UNLOCKED = "TOKEN_REFRESH_UNLOCKED"
}

export type USER_ACTION_TYPES = AuthAction | SimpleAction | FavoritesAction | AuthSourceTokenAction

export type AuthAction = {
  type: USER_ACTIONS.AUTH_USER_START | USER_ACTIONS.AUTH_USER_SUCCESS | USER_ACTIONS.AUTH_USER_ERROR,
  payload?: {
    user?: {
      id?: number
    } & ClusterApi.UserShort
    token: string
    msgCredentials?: {
      username?: string;
      password?: string;
    }
  },
  error: ApiError
}

export type AuthSourceTokenAction = {
  type: USER_ACTIONS.AUTH_SOURCE_TOKEN_SET,
  payload: AuthTokenData
}

export type SimpleAction = {
  type: USER_ACTIONS.LOGOUT | USER_ACTIONS.REWRITE_TOKEN | USER_ACTIONS.TOKEN_REFRESH_LOCKED | USER_ACTIONS.TOKEN_REFRESH_UNLOCKED,
  payload?: string
}

export type FavoritesAction = {
  type: USER_ACTIONS.FAVORITES_SET | USER_ACTIONS.FAVORITE_ADDED | USER_ACTIONS.FAVORITE_REMOVED,
  payload: number[] | number,
}

// Authenticate User
export const doAuthUser = (authToken?: string): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  dispatch({type: USER_ACTIONS.AUTH_USER_START});
  try {
    const data = await api.getNewToken(authToken);

    dispatch({
      type: USER_ACTIONS.AUTH_USER_SUCCESS,
      payload: data
    });

    return Promise.resolve();
  } catch (message) {
    // @TODO Catch major stuff like the API not being available.
    try {
      const error = JSON.parse(message);

      dispatch({
        type: USER_ACTIONS.AUTH_USER_ERROR,
        error
      });
      dispatch(doRoute('LOGIN'));
    } catch(badError) {
      dispatch({
        type: USER_ACTIONS.AUTH_USER_ERROR,
      });
    }
  }
}

export const doPostAuthUser = (): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  // Refresh our channel list.
  await dispatch(doGetChannels());

  // Register APN device if we're on one.
  dispatch(doRegisterApnDevice());

  const state = getState();

  const userIsGuest = selectIsUserGuest(state);

  // Firebase event
  if (userIsGuest) {
    firebase.analytics.setUserProperties({auth_src: 'guest'});
  } else {
    firebase.analytics.setUserId((state.user.user?.id!).toString());
    firebase.analytics.setUserProperties({auth_src: getJwtTokenPayload(state.user.token).authSrc});
  }

  firebase.analytics.logEvent("login", {});

  // Connect to messaging service and subscribe to alerts
  ClusterMessagesClient.connect((client) => {
    client.subscribe("alerts", selectChannelIds(state));
  });

  const deferredDestination = state.system.deferredDestination;

  if (typeof deferredDestination !== "undefined") {
    // Redirect to previously saved destination
    dispatch(redirect(deferredDestination));

    // Clear saved destination
    dispatch(doClearDeferredDestination());
  } else {
    // Redirect to home page.
    dispatch(doRoute('HOME', {}, true));
  }

  // Update favorite cells
  return dispatch(doGetFavoriteCells());
}

export const doRefreshToken = (userIsGuest: boolean, authSourceToken?: AuthTokenData): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  const state = getState();

  if (state.user.tokenRefreshLocked) {
    return Promise.resolve({skipped: true});
  }

  dispatch({
    type: USER_ACTIONS.TOKEN_REFRESH_LOCKED
  });

  if (userIsGuest) {
    // Auth and login user as guest
    return dispatch(doAuthUser()).then(() => {
      dispatch({
        type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
      });
    }).catch (error => {
      // Unlock refresher on error.
      dispatch({
        type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
      });

      try {
        const message = JSON.parse(error.message);
        return Promise.reject(message.userMessage);
      } catch(badError) {
        return Promise.reject("unknown");
      }
    });
  } else if (authSourceToken) {
    // Refresh auth token
    return api.refreshAuthSourceToken(authSourceToken).then(async authToken => {
      const { accessToken, refreshToken, data, tokenType } = authToken;
      // Save the auth token
      dispatch(doSetAuthSourceToken({ accessToken, refreshToken, data, tokenType }));
      // Auth and login user
      return dispatch(doAuthUser(authToken.accessToken)).then(() => {
        dispatch({
          type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
        });
      });
    }).catch (error => {
      // Unlock refresher on error.
      dispatch({
        type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
      });

      try {
        const message = JSON.parse(error.message);
        return Promise.reject(message.userMessage);
      } catch(badError) {
        return Promise.reject("unknown");
      }
    });
  } else {
    return Promise.reject("No Auth Source Token");
  }
}

export const doSetAuthSourceToken = (token: AuthTokenData) => {
  return {
    type: USER_ACTIONS.AUTH_SOURCE_TOKEN_SET,
    payload: token
  };
}


export const doLogoutUser = (message: string = "", userRequested: boolean = false): AppThunk => (dispatch) => {
  dispatch({
    type: USER_ACTIONS.LOGOUT
  });

  ClusterMessagesClient.disconnect();

  if (userRequested) {
    firebase.analytics.logEvent("logout", {});
  }

  dispatch(doRoute('LOGIN', {payload: {message}}, true));
}

export const doLocalSetFavoriteCells = (cellIds: number[]) => {
  return {
    type: USER_ACTIONS.FAVORITES_SET,
    payload: cellIds
  }
}

export const doLocalAddRemoveFavoriteCell = (cellId: number, op: "add" | "remove") => {
  return {
    type: op === "add" ? USER_ACTIONS.FAVORITE_ADDED : USER_ACTIONS.FAVORITE_REMOVED,
    payload: cellId
  }
}

export const doGetFavoriteCells = (): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  const userId = getState().user.user?.id;

  if (userId) {
    // Call the API and then set the favorites with the return.
    return api.getFavoriteCells(userId).then(favorites => {
      dispatch(doLocalSetFavoriteCells(favorites));
    }).catch((message) => {
      Log.error("Failed to get favorites " + message.message, "user");
    });
  }

  return Promise.resolve();
}

export const doAddRemoveFavoriteCell = (cellId: number, op: "add" | "remove"): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  const userId = getState().user.user?.id;

  // Add or remove the cell locally.
  dispatch(doLocalAddRemoveFavoriteCell(cellId, op));

  if (userId) {
    // Call the API and then set the favorites with the return.
    return api.updateFavoriteCells(userId, cellId, op, {headers: {"Content-Type": "application/json"}}).then(favorites => {
      dispatch(doLocalSetFavoriteCells(favorites));
    }).catch((message) => {
      Log.error("Failed to " + op + " favorite " + message.message, "user");
    });
  }

  return Promise.resolve();
}

export const doClearFavoriteCells = (): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  const userId = getState().user.user?.id;

  dispatch(doLocalSetFavoriteCells([]));

  // Firebase event
  firebase.analytics.logEvent("clear_favorites");

  if (userId) {
    // Call the API.
    return api.clearFavoriteCells(userId).catch((message) => {
      Log.error("Failed to clear favorites " + message.message, "user");
    });
  }

  return Promise.resolve();
}

export const doRegisterApnDevice = (): AppThunk<Promise<unknown>> => async (dispatch, getState, api) => {
  const state = getState();
  const userId = selectUser(state)?.id;
  const deviceId = selectDeviceId(state);

  if (typeof userId !== "undefined" && deviceId) {
    // Call the API.
    return api.registerApnDevice(userId, {deviceId}).catch((message) => {
      Log.error("Failed to register device " + message.message, "user");
    });
  }

  return Promise.resolve();
}